import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Button,
  Chip,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Help as HelpIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  Radar as RadarIcon,
  Speed as SpeedIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotificationScan } from '../../contexts/NotificationScanContext';

interface AppHeaderProps {
  onMenuToggle: () => void;
  drawerWidth: number;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ onMenuToggle, drawerWidth }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [hasShownScanNotification, setHasShownScanNotification] = useState(false);
  const [scanNotificationCount, setScanNotificationCount] = useState(0);
  const [scanMessage, setScanMessage] = useState<string | null>(null);
  const [lastNotificationDate, setLastNotificationDate] = useState<string | null>(null);
  
  // Account creation notifications
  const [accountNotifications, setAccountNotifications] = useState<Array<{
    id: string;
    title: string;
    message: string;
    timestamp: Date;
    accountName: string;
    type: 'success' | 'warning' | 'error' | 'info';
  }>>(() => {
    // Load from localStorage on initialization
    try {
      const saved = localStorage.getItem('accountNotifications');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
      }
    } catch (error) {
      console.error('Failed to load notifications from localStorage:', error);
    }
    return [];
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { themeMode, toggleTheme } = useTheme();
  const { user, signOut, redirectToCognitoSignOut } = useAuth();
  const { checkScanStart } = useNotificationScan();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleDismissScanNotification = () => {
    setScanNotificationCount(0);
    setScanMessage(null);
    setHasShownScanNotification(true);
    setLastNotificationDate(new Date().toDateString()); // Track the date when dismissed
  };

  const addAccountNotification = (title: string, message: string, accountName: string, type: 'success' | 'warning' | 'error' | 'info' = 'success') => {
    const notification = {
      id: `account-${Date.now()}`,
      title,
      message,
      timestamp: new Date(),
      accountName,
      type
    };
    setAccountNotifications(prev => {
      const newNotifications = [notification, ...prev.slice(0, 4)]; // Keep only last 5
      // Save to localStorage
      try {
        localStorage.setItem('accountNotifications', JSON.stringify(newNotifications));
      } catch (error) {
        console.error('Failed to save notifications to localStorage:', error);
      }
      return newNotifications;
    });
  };

  const removeAccountNotification = (id: string) => {
    setAccountNotifications(prev => {
      const newNotifications = prev.filter(n => n.id !== id);
      // Save to localStorage
      try {
        localStorage.setItem('accountNotifications', JSON.stringify(newNotifications));
      } catch (error) {
        console.error('Failed to save notifications to localStorage:', error);
      }
      return newNotifications;
    });
  };

  // Expose functions globally for use in other components
  useEffect(() => {
    (window as any).addAccountNotification = addAccountNotification;
    (window as any).removeAccountNotification = removeAccountNotification;
    
    return () => {
      delete (window as any).addAccountNotification;
      delete (window as any).removeAccountNotification;
    };
  }, []);

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationAnchor(null);
  };

  const getPageTitle = () => {
    const pathMap: Record<string, string> = {
      '/': 'Overview',
      '/inventory': 'Resource Inventory',
      '/rules': 'Tagging Rules',
      '/naming-conventions': 'Naming Conventions',
      '/aws-accounts': 'AWS Accounts',
      '/settings': 'Settings',
    };
    return pathMap[location.pathname] || 'TagOps';
  };

  const handleLogout = async () => {
    try {
      // Use the proper signOut flow which clears localStorage and redirects to Cognito
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleMenuClose();
  };

  const handletagopsClick = () => {
    navigate('/');
  };

  // Function to check if scan should start and show notification
  const handleScanStartCheck = useCallback(async () => {
    try {
      const today = new Date().toDateString();
      
      // Don't show notification if already shown today
      if (hasShownScanNotification && lastNotificationDate === today) {
        return;
      }

      const result = await checkScanStart();
      
      if (result.shouldShowNotification && result.scanMessage) {
        setHasShownScanNotification(true);
        setScanNotificationCount(1);
        setScanMessage(result.scanMessage);
        setLastNotificationDate(today);
      }
    } catch (error) {
      console.error('Failed to check scan start:', error);
    }
  }, [checkScanStart, hasShownScanNotification, lastNotificationDate]);

  // Monitor scan start periodically
  useEffect(() => {
    // Check immediately
    handleScanStartCheck();
    
    // Check every 30 seconds
    const interval = setInterval(handleScanStartCheck, 30000);
    
    return () => clearInterval(interval);
  }, [handleScanStartCheck]);

  const getUserDisplayName = () => {
    if (!user) return 'User';
    
    // Try to use first and last name if available
    if (user.attributes.given_name && user.attributes.family_name) {
      return `${user.attributes.given_name} ${user.attributes.family_name}`;
    }
    
    // Fallback to first name only
    if (user.attributes.given_name) {
      return user.attributes.given_name;
    }
    
    // Fallback to username or email
    return user.username || user.email || 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        background: themeMode === 'dark' 
          ? 'rgba(30, 41, 59, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: themeMode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(0, 0, 0, 0.05)',
      }}
      elevation={0}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMenuToggle}
          sx={{ 
            mr: 2, 
            display: { sm: 'none' },
            color: 'text.primary',
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '1.25rem',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.main',
                transition: 'color 0.2s ease-in-out',
              },
            }}
            onClick={handletagopsClick}
          >
            {getPageTitle()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Theme Toggle */}
          <Tooltip title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              sx={{ color: 'text.secondary' }}
            >
              {themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              color="inherit"
              onClick={handleNotificationMenuOpen}
              sx={{ color: 'text.secondary' }}
            >
              <Badge badgeContent={scanNotificationCount + accountNotifications.length} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Help */}
          <Tooltip title="Help & Documentation">
            <IconButton
              color="inherit"
              sx={{ color: 'text.secondary' }}
              onClick={() => {
                const docUrl = process.env.REACT_APP_DOCUMENTATION_URL || 'https://tagops.cloud/documentation';
                window.open(`${docUrl}/index.html`, '_blank');
              }}
            >
              <HelpIcon />
            </IconButton>
          </Tooltip>



          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
              <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {getUserDisplayName()}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {user?.tenantId ? `Tenant: ${user.tenantId}` : 'User'}
              </Typography>
            </Box>
            
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{ p: 0 }}
              >
                <Avatar
                  sx={{ 
                    width: 40, 
                    height: 40,
                    bgcolor: 'primary.main',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {getUserInitials()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
              borderRadius: 2,
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {getUserDisplayName()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {user?.email || 'No email'}
            </Typography>
            {user?.tenantId && (
              <Chip
                label={`Tenant: ${user.tenantId}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 1, fontSize: '0.75rem' }}
              />
            )}
            {user?.role && (
              <Chip
                label={`Role: ${user.role}`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ mt: 1, fontSize: '0.75rem' }}
              />
            )}
          </Box>
          
          <Divider />
          
          
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
            Sign Out
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 350,
              maxHeight: 400,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
              borderRadius: 2,
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
          </Box>
          
          <Box sx={{ p: 0 }}>
             {/* Account Notifications */}
             {accountNotifications.map((notification) => (
               <Box key={notification.id} sx={{ 
                 display: 'flex', 
                 alignItems: 'flex-start', 
                 justifyContent: 'space-between',
                 p: 2,
                 minHeight: notification.type !== 'warning' && !notification.title.includes('Deleted Successfully') ? 120 : 80,
                 borderBottom: '1px solid',
                 borderColor: 'divider',
                 '&:hover': {
                   bgcolor: 'action.hover'
                 }
               }}>
                 <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                   <Box sx={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     justifyContent: 'center',
                     width: 36,
                     height: 36,
                     borderRadius: '50%',
                     bgcolor: notification.type === 'warning' ? 'warning.main' : 
                              notification.type === 'error' ? 'error.main' :
                              notification.type === 'info' ? 'info.main' : 'success.main',
                     color: 'white',
                     boxShadow: notification.type === 'warning' ? '0 2px 8px rgba(255, 152, 0, 0.3)' :
                                notification.type === 'error' ? '0 2px 8px rgba(244, 67, 54, 0.3)' :
                                notification.type === 'info' ? '0 2px 8px rgba(33, 150, 243, 0.3)' :
                                '0 2px 8px rgba(76, 175, 80, 0.3)',
                     flexShrink: 0
                   }}>
                     <CloudIcon sx={{ fontSize: '1.2rem' }} />
                   </Box>
                   <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                     <Box sx={{ flex: 1 }}>
                       <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, wordWrap: 'break-word' }}>
                         {notification.title}
                       </Typography>
                       <Typography variant="body2" sx={{ 
                         color: 'text.secondary', 
                         fontSize: '0.85rem', 
                         wordWrap: 'break-word', 
                         whiteSpace: 'pre-wrap', 
                         overflowWrap: 'break-word',
                         lineHeight: 1.4,
                         maxHeight: '3.5em',
                         overflow: 'hidden',
                         display: '-webkit-box',
                         WebkitLineClamp: 3,
                         WebkitBoxOrient: 'vertical'
                       }}>
                         {notification.message}
                       </Typography>
                     </Box>
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                       {((notification.type === 'success' || notification.type === 'info' || notification.type === 'warning') && !notification.title.includes('Deleted Successfully') && !notification.title.includes('Account Creation Started')) || notification.title.includes('Account Added with CloudTrail Warning') ? (
                         <Button
                           size="small"
                           variant="outlined"
                           onClick={() => navigate('/settings?tab=account-settings')}
                           sx={{ fontSize: '0.75rem', py: 0.5, px: 1, alignSelf: 'flex-start' }}
                         >
                           Configure
                         </Button>
                       ) : null}
                       <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                         {notification.timestamp.toLocaleTimeString()}
                       </Typography>
                     </Box>
                   </Box>
                 </Box>
                 <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-start' }}>
                   <IconButton
                     size="small"
                     onClick={() => removeAccountNotification(notification.id)}
                     sx={{ 
                       color: 'text.secondary',
                       '&:hover': {
                         color: 'text.primary',
                         bgcolor: 'action.hover'
                       }
                     }}
                   >
                     <CloseIcon fontSize="small" />
                   </IconButton>
                 </Box>
               </Box>
             ))}

             {/* Scan Notifications */}
             {scanMessage && (
               <Box sx={{ 
                 display: 'flex', 
                 alignItems: 'flex-start', 
                 justifyContent: 'space-between',
                 p: 2,
                 minHeight: 80,
                 borderBottom: '1px solid',
                 borderColor: 'divider',
                 '&:hover': {
                   bgcolor: 'action.hover'
                 }
               }}>
                 <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                   <Box sx={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     justifyContent: 'center',
                     width: 36,
                     height: 36,
                     borderRadius: '50%',
                     bgcolor: 'primary.main',
                     color: 'white',
                     boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                     flexShrink: 0
                   }}>
                     <RadarIcon sx={{ fontSize: '1.2rem' }} />
                   </Box>
                   <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                     <Box sx={{ flex: 1 }}>
                       <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, wordWrap: 'break-word' }}>
                         Scan Started
                       </Typography>
                       <Typography variant="body2" sx={{ 
                         color: 'text.secondary', 
                         fontSize: '0.85rem',
                         wordWrap: 'break-word', 
                         whiteSpace: 'pre-wrap', 
                         overflowWrap: 'break-word',
                         lineHeight: 1.4,
                         maxHeight: '3.5em',
                         overflow: 'hidden',
                         display: '-webkit-box',
                         WebkitLineClamp: 3,
                         WebkitBoxOrient: 'vertical'
                       }}>
                         {scanMessage}
                       </Typography>
                     </Box>
                     <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem', mt: 1 }}>
                       {new Date().toLocaleTimeString()}
                     </Typography>
                   </Box>
                 </Box>
                 <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-start' }}>
                   <IconButton
                     size="small"
                     onClick={handleDismissScanNotification}
                     sx={{ 
                       color: 'text.secondary',
                       '&:hover': {
                         color: 'text.primary',
                         bgcolor: 'action.hover'
                       }
                     }}
                   >
                     <CloseIcon fontSize="small" />
                   </IconButton>
                 </Box>
               </Box>
             )}

            {/* No notifications message */}
            {!scanMessage && accountNotifications.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                  No new notifications
                </Typography>
              </Box>
            )}
          </Box>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}; 